import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SmileyWink, Play, ShuffleAngular } from "@phosphor-icons/react";
import api from "../lib/api";
import MediaThumb from "./MediaThumb";
import InAppVideoPlayer from "./InAppVideoPlayer";

// Late Night with Holy Words — auto-shuffled comedy queue that surfaces
// only between 8 PM and 2 AM local time. Same deterministic seed for
// every viewer (server-side date-seeded shuffle) so the rail is a
// shared cultural moment rather than a personalized pick. Plays the
// full queue in InAppVideoPlayer's onNext chain.
export default function LateNightComedyRail() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);

  // Hours-based visibility — 8 PM through 1:59 AM local time. Anyone
  // outside that window sees nothing (the rail unmounts cleanly).
  const now = new Date();
  const hour = now.getHours();
  const visible = hour >= 20 || hour < 2;

  useEffect(() => {
    if (!visible) return undefined;
    let alive = true;
    api
      .get("/comedy/late-night", { params: { count: 6 } })
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [visible]);

  if (!visible || items.length === 0) return null;

  const active = activeIdx >= 0 ? items[activeIdx] : null;
  const goNext = () => {
    if (activeIdx + 1 < items.length) setActiveIdx(activeIdx + 1);
    else setActiveIdx(-1);
  };

  return (
    <section
      data-testid="late-night-comedy-rail"
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-stone-900 via-stone-950 to-rose-950 text-cream p-6 md:p-8"
    >
      {/* Subtle decorative glow */}
      <div className="pointer-events-none absolute -top-10 -right-10 h-48 w-48 rounded-full bg-amber-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-12 -left-10 h-48 w-48 rounded-full bg-rose-600/20 blur-3xl" />

      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-bold text-amber-300">
            <SmileyWink weight="fill" size={12} /> Late Night with Holy Words
          </div>
          <h2 className="font-heading text-2xl md:text-3xl mt-1 leading-tight">
            Tonight at {hour >= 20 ? `${hour - 12} PM` : `${hour === 0 ? 12 : hour} AM`} ·
            <span className="ml-2 text-amber-200">Comedy lineup</span>
          </h2>
          <p className="mt-1 text-xs md:text-sm text-cream/70 max-w-md leading-relaxed">
            {items.length} clean, family-friendly clips queued up. Hit play, kick back,
            and let it roll until you drift off.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveIdx(0)}
            data-testid="late-night-play-all"
            className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-amber-300 text-stone-900 font-bold text-xs tracking-[0.18em] uppercase shadow-xl hover:bg-amber-200 active:scale-95 transition"
          >
            <Play size={14} weight="fill" /> Play all
          </button>
          <button
            type="button"
            onClick={() => navigate("/comedy")}
            data-testid="late-night-see-all"
            className="inline-flex items-center gap-1.5 px-4 h-11 rounded-full bg-cream/10 border border-cream/30 text-cream font-bold text-[11px] tracking-[0.18em] uppercase hover:bg-cream/15 active:scale-95 transition"
          >
            <ShuffleAngular size={14} weight="bold" /> See all
          </button>
        </div>
      </div>

      <div
        className="relative mt-6 flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory"
        data-testid="late-night-rail-strip"
      >
        {items.map((it, i) => (
          <button
            key={it.id}
            type="button"
            onClick={() => setActiveIdx(i)}
            data-testid={`late-night-card-${i}`}
            className="group shrink-0 w-44 md:w-52 snap-start text-left rounded-2xl overflow-hidden bg-stone-900/50 border border-cream/10 hover:border-amber-300/50 transition-all"
          >
            <div className="aspect-video relative overflow-hidden">
              <MediaThumb
                youtube_id={it.youtube_id}
                poster={it.poster}
                title={it.title}
                subtitle={it.artist}
                kind="comedy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <span className="absolute top-1.5 left-1.5 text-[9px] tracking-[0.2em] uppercase font-bold bg-amber-300 text-stone-900 px-2 py-0.5 rounded-full">
                #{i + 1}
              </span>
              <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition">
                <span className="w-10 h-10 rounded-full bg-amber-300 text-stone-900 grid place-items-center">
                  <Play size={16} weight="fill" />
                </span>
              </span>
            </div>
            <div className="p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-amber-300/80 truncate">
                {it.artist}
              </div>
              <div className="text-xs font-semibold mt-0.5 leading-snug line-clamp-2 text-cream">
                {it.title}
              </div>
            </div>
          </button>
        ))}
      </div>

      <InAppVideoPlayer
        open={!!active}
        onClose={() => setActiveIdx(-1)}
        item={active}
        queue={items}
        bucket="comedy"
        onSelect={(t) => {
          const idx = items.findIndex((x) => x.id === t.id);
          if (idx >= 0) setActiveIdx(idx);
        }}
        onNext={goNext}
        autoStart
      />
    </section>
  );
}
